import { useState } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';
import { NavigationHeader } from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProductFormModal from './components/ProductFormModal';
import { useProducts } from '../../contexts/ProductContext';

type SortField = 'name' | 'group' | 'unitPrice';
type SortDirection = 'asc' | 'desc';

const ProductCatalogManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(undefined);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { products, productGroups, addProduct, updateProduct, deleteProduct, loading, error } = useProducts();

  const handleProductDelete = (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteProduct(productId);
    }
  };

  const handleSaveProduct = (productData: any) => {
    if (editingProduct) {
      updateProduct(editingProduct.id, productData);
    } else {
      const newProduct = {
        id: `p${Date.now()}`,
        productName: productData.productName || '',
        groupName: productData.groupName || '',
        unitPrice: productData.unitPrice || 0,
        description: productData.description || '',
      };
      addProduct(newProduct);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(undefined);
    setIsProductModalOpen(true);
  };

  const handleEditProduct = (product: any) => {
    setEditingProduct(product);
    setIsProductModalOpen(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortField) {
      case 'name':
        aValue = (a.productName || '').toLowerCase();
        bValue = (b.productName || '').toLowerCase();
        break;
      case 'group':
        aValue = (a.groupName || '').toLowerCase();
        bValue = (b.groupName || '').toLowerCase();
        break;
      case 'unitPrice':
        aValue = a.unitPrice || 0;
        bValue = b.unitPrice || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-secondary opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <Icon name="ArrowUp" size={14} className="text-primary" />
    ) : (
      <Icon name="ArrowDown" size={14} className="text-primary" />
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <div
          className={`transition-all duration-300 ${
            isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
          }`}
        >
          <NavigationHeader />
          <main className="p-6">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
                  <p className="text-text-secondary">Loading products...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MainSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <div
          className={`transition-all duration-300 ${
            isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
          }`}
        >
          <NavigationHeader />
          <main className="p-6">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
                  <p className="text-text-primary font-semibold mb-2">Error Loading Products</p>
                  <p className="text-text-secondary">{error}</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <NavigationHeader />

        <main className="p-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-text-primary mb-1">
                  Product Catalog
                </h1>
                <p className="text-text-secondary">
                  Manage your product catalog
                </p>
              </div>
              <Button iconName="Plus" onClick={handleAddProduct}>
                Add Product
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th 
                        className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-muted/80 transition-colors select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Product</span>
                          {renderSortIcon('name')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-muted/80 transition-colors select-none"
                        onClick={() => handleSort('group')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Group</span>
                          {renderSortIcon('group')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-right text-sm font-semibold text-text-primary cursor-pointer hover:bg-muted/80 transition-colors select-none"
                        onClick={() => handleSort('unitPrice')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          <span>Unit Price</span>
                          {renderSortIcon('unitPrice')}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-text-primary w-32">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sortedProducts.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <Icon
                            name="Package"
                            size={48}
                            className="text-text-secondary mb-4 mx-auto"
                          />
                          <p className="text-text-secondary text-sm">No products found</p>
                        </td>
                      </tr>
                    ) : (
                      sortedProducts.map((product) => (
                        <tr
                          key={product.id}
                          className="hover:bg-muted/50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium text-text-primary">
                              {product.productName}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                              {product.groupName}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-sm font-semibold text-text-primary">
                              {formatPrice(product.unitPrice)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Edit"
                                onClick={() => handleEditProduct(product)}
                                aria-label={`Edit ${product.productName}`}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Trash2"
                                onClick={() => handleProductDelete(product.id)}
                                className="hover:text-error hover:bg-error/10"
                                aria-label={`Delete ${product.productName}`}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ProductFormModal
        isOpen={isProductModalOpen}
        onClose={() => {
          setIsProductModalOpen(false);
          setEditingProduct(undefined);
        }}
        onSave={handleSaveProduct}
        product={editingProduct}
        groups={productGroups}
      />
    </div>
  );
};

export default ProductCatalogManagement;